<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-content">
      <SettingsNav />

      <form @submit.prevent="changePassword">
        <div class="sm:grid sm:grid-cols-2 sm:gap-[25px]">
          <div class="mb-[20px] sm:mb-0 relative" id="passwordHideShow">
            <label
              class="mb-[10px] text-black dark:text-white font-medium block"
            >
              {{ directT('settings.changePassword.oldPassword') }}
            </label>
            <input
              class="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
              id="password"
              :placeholder="directT('settings.changePassword.typePlaceholder')"
              v-model="oldPassword"
              :type="showPassword1 ? 'text' : 'password'"
              required
            />
            <button
              class="absolute text-lg ltr:right-[20px] rtl:left-[20px] bottom-[13px] transition-all hover:text-primary-500"
              type="button"
              @click="togglePasswordVisibility(1)"
            >
              <i :class="showPassword1 ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
            </button>
          </div>
          <div class="mb-[20px] sm:mb-0 relative" id="passwordHideShow2">
            <label
              class="mb-[10px] text-black dark:text-white font-medium block"
            >
              {{ directT('settings.changePassword.newPassword') }}
            </label>
            <input
              class="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
              id="password2"
              :placeholder="directT('settings.changePassword.typePlaceholder')"
              v-model="newPassword"
              :type="showPassword2 ? 'text' : 'password'"
              required
              minlength="6"
            />
            <button
              class="absolute text-lg ltr:right-[20px] rtl:left-[20px] bottom-[13px] transition-all hover:text-primary-500"
              type="button"
              @click="togglePasswordVisibility(2)"
            >
              <i :class="showPassword2 ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
            </button>
          </div>
          <div
            class="sm:col-span-2 mb-[20px] sm:mb-0 relative"
            id="passwordHideShow3"
          >
            <label
              class="mb-[10px] text-black dark:text-white font-medium block"
            >
              {{ directT('settings.changePassword.confirmPassword') }}
            </label>
            <input
              class="h-[55px] rounded-md text-black dark:text-white border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] block w-full outline-0 transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-primary-500"
              id="password3"
              :placeholder="directT('settings.changePassword.typePlaceholder')"
              v-model="confirmPassword"
              :type="showPassword3 ? 'text' : 'password'"
              required
              minlength="6"
            />
            <button
              class="absolute text-lg ltr:right-[20px] rtl:left-[20px] bottom-[13px] transition-all hover:text-primary-500"
              type="button"
              @click="togglePasswordVisibility(3)"
            >
              <i :class="showPassword3 ? 'ri-eye-line' : 'ri-eye-off-line'"></i>
            </button>
            <p v-if="passwordMismatch" class="text-danger-500 mt-2">
              {{ directT('settings.changePassword.passwordsDoNotMatch') }}
            </p>
          </div>
        </div>
        
        <div v-if="successMessage || errorMessage" class="mt-4 sm:col-span-2">
          <div v-if="successMessage" class="p-3 bg-green-100 text-green-700 rounded-md">
            {{ successMessage }}
          </div>
          <div v-if="errorMessage" class="p-3 bg-red-100 text-red-700 rounded-md">
            {{ errorMessage }}
          </div>
        </div>
        
        <div class="mt-[20px] md:mt-[25px]">
          <button
            type="submit"
            class="font-medium inline-block transition-all rounded-md md:text-md py-[10px] md:py-[12px] px-[20px] md:px-[22px] bg-primary-500 text-white hover:bg-primary-400"
            :disabled="isLoading"
          >
            <span class="inline-block relative ltr:pl-[29px] rtl:pr-[29px]">
              <i
                class="material-symbols-outlined ltr:left-0 rtl:right-0 absolute top-1/2 -translate-y-1/2"
              >
                check
              </i>
              {{ isLoading ? directT('settings.changePassword.updating') : directT('settings.changePassword.updatePassword') }}
            </span>
          </button>
          <RouterLink
            to="/authentication/forgot-password"
            class="inline-block text-danger-500 ltr:ml-[23px] rtl:mr-[23px]"
          >
            {{ directT('settings.changePassword.forgotPassword') }}
          </RouterLink>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { RouterLink } from 'vue-router';
import SettingsNav from "../SettingsNav.vue";
import { supabase } from '@/utils/supabase';
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

type TranslationRecord = Record<string, any>;
type TranslationsType = Record<string, TranslationRecord>;

const allTranslations: TranslationsType = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations,
};

export default defineComponent({
  name: "ChangePassword",
  components: {
    SettingsNav,
    RouterLink
  },
  setup() {
    const { t, locale } = useI18n();

    // Direct translation function with fallback
    function directT(key: string): string {
      const parts = key.split('.');
      let result: any = allTranslations[locale.value] || allTranslations.en;
      
      for (const part of parts) {
        if (result && typeof result === 'object' && part in result) {
          result = result[part];
        } else {
          // Fallback to English if key doesn't exist
          let englishResult = allTranslations.en;
          for (const engPart of parts) {
            if (englishResult && typeof englishResult === 'object' && engPart in englishResult) {
              englishResult = englishResult[engPart];
            } else {
              return key; // Return the key itself if not found in English either
            }
          }
          return typeof englishResult === 'string' ? englishResult : key;
        }
      }
      
      return typeof result === 'string' ? result : key;
    }

    // משתנים לשמירת הסיסמאות
    const oldPassword = ref('');
    const newPassword = ref('');
    const confirmPassword = ref('');
    
    // משתנים לניהול מצב הצגת הסיסמה
    const showPassword1 = ref(false);
    const showPassword2 = ref(false);
    const showPassword3 = ref(false);
    
    // משתנים לניהול מצב הטופס
    const isLoading = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    
    // חישוב שגיאת אי התאמה בין הסיסמאות
    const passwordMismatch = computed(() => {
      return newPassword.value && confirmPassword.value && 
             newPassword.value !== confirmPassword.value;
    });
    
    // פונקציה להחלפת מצב הצגת הסיסמה
    const togglePasswordVisibility = (passwordField: number) => {
      if (passwordField === 1) showPassword1.value = !showPassword1.value;
      else if (passwordField === 2) showPassword2.value = !showPassword2.value;
      else if (passwordField === 3) showPassword3.value = !showPassword3.value;
    };
    
    // פונקציה לשינוי הסיסמה
    const changePassword = async () => {
      // איפוס הודעות
      successMessage.value = '';
      errorMessage.value = '';
      
      // בדיקה שהסיסמאות החדשות תואמות
      if (newPassword.value !== confirmPassword.value) {
        errorMessage.value = directT('settings.changePassword.errorPasswordsMismatch');
        return;
      }
      
      // בדיקת אורך סיסמה מינימלי
      if (newPassword.value.length < 6) {
        errorMessage.value = directT('settings.changePassword.errorPasswordLength');
        return;
      }
      
      try {
        isLoading.value = true;
        
        // קבלת המשתמש הנוכחי
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          errorMessage.value = directT('settings.changePassword.errorUserNotLoggedIn');
          return;
        }
        
        // התחברות מחדש עם הסיסמה הנוכחית כדי לוודא שהיא נכונה
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: session.user.email || '',
          password: oldPassword.value,
        });
        
        if (signInError) {
          errorMessage.value = directT('settings.changePassword.errorWrongCurrentPassword');
          return;
        }
        
        // שינוי הסיסמה
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword.value,
        });
        
        if (updateError) {
          errorMessage.value = directT('settings.changePassword.errorUpdatingPassword') + ': ' + updateError.message;
          return;
        }
        
        // איפוס הטופס
        oldPassword.value = '';
        newPassword.value = '';
        confirmPassword.value = '';
        
        successMessage.value = directT('settings.changePassword.successMessage');
      } catch (error) {
        console.error('שגיאה בעדכון סיסמה:', error);
        errorMessage.value = directT('settings.changePassword.errorUnexpected');
      } finally {
        isLoading.value = false;
      }
    };
    
    return {
      oldPassword,
      newPassword,
      confirmPassword,
      showPassword1,
      showPassword2,
      showPassword3,
      isLoading,
      successMessage,
      errorMessage,
      passwordMismatch,
      togglePasswordVisibility,
      changePassword,
      directT
    };
  },
});
</script>
